import _jsx from '@babel/runtime/helpers/jsx';
import React, { useEffect, useState, useMemo } from 'react';
import 'prop-types';
import AndesTooltip from '@andes/tooltip';
import { trackPage, trackEvent, hyphenatedbem, getDisplayName, getExpiration, setCookie, getCookie, isSupported, withMappedProps } from '@vpp-frontend-components/common';
import { useViewCounter } from '@vpp-frontend-components/hooks';
import _extends from '@babel/runtime/helpers/extends';
import classnames from 'classnames';
import { ComponentList } from '@vpp-frontend-components/component-list';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import Button from '@andes/button';
import { StyledLabel, labelToProps } from '@vpp-frontend-components/styled-label';
import get from 'lodash/get';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import { useVppContext } from '@vpp-frontend-components/vpp-context';
import { ImageComponent, imageToProps } from '@vpp-frontend-components/image';
import { Media, mediaToProps } from '@vpp-frontend-components/media';
const SIDE$1 = {
  TOP: 'top',
  BOTTOM: 'bottom',
  LEFT_TOP: 'leftTop',
  RIGHT_TOP: 'rightTop',
  BOTTOM_RIGHT: 'bottomRight',
  BOTTOM_LEFT: 'bottomLeft',
  TOP_LEFT: 'topLeft',
  TOP_RIGHT: 'topRight',
  LEFT: 'left',
  RIGHT: 'right'
};
const TYPE$1 = {
  LIGHT: 'light',
  DARK: 'dark',
  HIGHLIGHT: 'highlight'
};
const TRIGGER$2 = {
  CLICK: 'click',
  HOVER: 'hover',
  NO_TRIGGER: 'no-trigger'
};
const TOOLTIP_CONSTANTS = {
  SIDE: SIDE$1,
  TYPE: TYPE$1,
  TRIGGER: TRIGGER$2
};
const {
  SIDE,
  TRIGGER: TRIGGER$1,
  TYPE
} = TOOLTIP_CONSTANTS;

// reference: https://furydocs.io/frontend-andes-ui/latest/guide/?path=/story/tooltip--andes-tooltip

const Tooltip = _ref => {
  let {
    id = undefined,
    storable = false,
    onClose = () => {},
    className,
    title = '',
    content = null,
    side = SIDE.TOP,
    trigger = TRIGGER$1.HOVER,
    close_track = null,
    track = null,
    visible = undefined,
    open = undefined,
    showCloseButton = true,
    closable = true,
    children = null,
    offsetX = 0,
    offsetY = 0,
    type = TYPE.LIGHT,
    onOpen = () => {},
    mouseLeaveDelay = 0.1
  } = _ref;
  // migration andes8
  // eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle
  const _open = open !== null && open !== void 0 ? open : visible;
  // eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle
  const _closable = closable && showCloseButton;
  const updateCloseStatus = useViewCounter({
    id,
    visible: _open,
    storable,
    cookieName: 'tooltips-configuration'
  });
  useEffect(() => {
    trackPage(track);
  }, [track]);
  const handleCloseTooltip = () => {
    if (storable) {
      updateCloseStatus();
    }
    trackEvent(close_track);
    onClose();
  };
  return /*#__PURE__*/_jsx(AndesTooltip, {
    onClose: handleCloseTooltip,
    onOpen: onOpen,
    className: className,
    title: title,
    content: content,
    side: side,
    trigger: trigger,
    open: _open,
    closable: _closable,
    offsetX: offsetX,
    offsetY: offsetY,
    type: type.toLowerCase(),
    mouseLeaveDelay: mouseLeaveDelay,
    appendTo: () => document.body
  }, `${trigger}-${id}`, children);
};
const TooltipContent = _ref => {
  let {
    content = null,
    components = null,
    availableComponents
  } = _ref;
  if (components) {
    return /*#__PURE__*/_jsx(ComponentList, {
      availableComponents: availableComponents,
      initialState: {
        components
      },
      modifier: "tooltip"
    });
  }
  return content;
};
const {
  TRIGGER
} = TOOLTIP_CONSTANTS;
const namespace$1 = 'ui-vpp-tooltip';
const TooltipWrapper = _ref => {
  let {
    tooltip = null,
    className = '',
    children = null
  } = _ref;
  if (!tooltip) {
    return children;
  }
  const NOT_TRIGGER = tooltip.behaviour === 'FIXED';
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const is_information_tooltip = Boolean(
  // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
  tooltip.content && tooltip.content.props && tooltip.content.props.information_tooltip);
  return /*#__PURE__*/React.createElement(Tooltip, _extends({
    className: classnames(className, namespace$1, hyphenatedbem(tooltip.id)),
    trigger: NOT_TRIGGER ? TRIGGER.NO_TRIGGER : TRIGGER.HOVER,
    side: tooltip.side,
    storable: tooltip.storable,
    type: tooltip.tooltip_type,
    id: tooltip.id,
    track: tooltip.track,
    close_track: tooltip.close_track
  }, NOT_TRIGGER ? {} : {
    open: tooltip === null || tooltip === void 0 ? void 0 : tooltip.visible
  }, {
    closable: is_information_tooltip ? tooltip.showCloseButton : tooltip.behaviour === 'FIXED',
    content: /*#__PURE__*/_jsx(TooltipContent, {
      availableComponents: tooltip.availableComponents,
      content: tooltip.content,
      components: tooltip.components
    }),
    offsetX: tooltip.offsetX,
    offsetY: tooltip.offsetY,
    mouseLeaveDelay: tooltip.mouseLeaveDelay,
    onClose: tooltip.onCloseTooltip || tooltip.onClose
  }), children);
};
const _excluded = ["tooltip"];

/**
 * withTooltip
 */
const withTooltip = function (WrappedComponent) {
  let className = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';
  const withTooltipComponent = _ref => {
    let {
        tooltip = null
      } = _ref,
      props = _objectWithoutProperties(_ref, _excluded);
    return /*#__PURE__*/_jsx(TooltipWrapper, {
      tooltip: tooltip,
      className: className
    }, void 0, /*#__PURE__*/React.createElement(WrappedComponent, props));
  };
  withTooltipComponent.displayName = `withTooltipComponent(${getDisplayName(WrappedComponent)})`;
  return withTooltipComponent;
};
const localStorageShippingTooltipId$1 = 'TOOLTIP_NO_FREE_SHIPPING';
const shippingActionTooltip = 'CONFIRM_SHIPPING_TOOLTIP';
const handleTooltipDefaultAction = (actionType, toggleVisibility) => {
  if (actionType === shippingActionTooltip) {
    localStorage.setItem(localStorageShippingTooltipId$1, Math.floor(Date.now() / 1000));
    toggleVisibility(false);
  }
};
const namespace = 'ui-pdp__info-tooltip';
const InformationTooltipContent = _ref => {
  let {
    information_tooltip = null,
    toggleVisibility = null
  } = _ref;
  if (!information_tooltip) {
    return null;
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(StyledLabel, _extends({
    as: "span"
  }, information_tooltip.label, {
    className: `${namespace}__text`
  })), /*#__PURE__*/_jsx("div", {
    className: `${namespace}__ctas-container`
  }, void 0, !!(information_tooltip !== null && information_tooltip !== void 0 && information_tooltip.primary_button) && /*#__PURE__*/_jsx(Button, {
    size: "small",
    "data-testid": "tooltip-button",
    onClick: () => handleTooltipDefaultAction(information_tooltip.primary_button.action_type, toggleVisibility)
  }, void 0, /*#__PURE__*/React.createElement(StyledLabel, _extends({
    as: "span"
  }, information_tooltip.primary_button.label))), /*#__PURE__*/React.createElement(StyledLabel, _extends({
    as: "a",
    href: information_tooltip.primary_link.target,
    target: "_blank",
    className: classnames(`${namespace}__link`, {
      [`${namespace}__withoutMargin`]: !(information_tooltip !== null && information_tooltip !== void 0 && information_tooltip.primary_button)
    })
  }, information_tooltip.primary_link.label))));
};
const cookieName = 'tooltips-configuration';
const getTooltipCookie = tooltipId => {
  const cookie = getCookie(cookieName);
  return get(cookie, `${cookieName}.${tooltipId}`, {});
};
const useTooltipCookie = _ref => {
  let {
    id,
    visible,
    expirationDays = 365
  } = _ref;
  const [isVisible, setIsVisible] = useState();
  const {
    tootipId,
    exDays
  } = useMemo(() => ({
    tootipId: id,
    exDays: expirationDays
  }), [id, expirationDays]);
  useEffect(() => {
    const expires = getExpiration(exDays);
    if (typeof visible === 'boolean') {
      const status = {
        hidden: !visible,
        expires
      };
      setCookie(cookieName, {
        [tootipId]: status
      }, {
        expires
      });
      setIsVisible(visible);
    } else {
      const {
        hidden
      } = getTooltipCookie(tootipId);
      const status = {
        hidden: true,
        expires
      };
      setCookie(cookieName, {
        [tootipId]: status
      }, {
        expires
      });
      setIsVisible(typeof hidden === 'boolean' ? !hidden : true);
    }
  }, [tootipId, exDays, visible]);
  const setVisibilityTooltip = function (newVisible) {
    let time = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : exDays;
    if (!newVisible) {
      const expires = getExpiration(time);
      const status = {
        hidden: !newVisible,
        expires
      };
      setCookie(cookieName, {
        [tootipId]: status
      }, {
        expires
      });
      setIsVisible(newVisible);
    }
  };
  return [isVisible, setVisibilityTooltip];
};
function useTooltipWithFrequencyStatus(tooltipId, timeFreq) {
  // eslint-disable-next-line react/hook-use-state
  const [showTooltip, toggleTooltipVisibility] = useState(false);
  useEffect(() => {
    isSupported().then(() => {
      const localStorageValue = localStorage.getItem(tooltipId);
      const lastShownTimeDiff = localStorageValue ? Math.floor(Date.now() / 1000) - localStorageValue : 0;
      if (localStorageValue && timeFreq < 0) {
        return;
      }
      if (!localStorageValue || lastShownTimeDiff > timeFreq) {
        toggleTooltipVisibility(true);
      }
    }).catch(() => null);
  }, [tooltipId, timeFreq]);
  return [showTooltip, toggleTooltipVisibility];
}
const TOOLTIP_SIDE = {
  desktop: 'left',
  mobile: 'bottom'
};
const GAP = -45;
function useTooltipUtils() {
  const {
    staticProps
  } = useVppContext();
  const {
    deviceType
  } = staticProps;
  const isDesktop = deviceType === 'desktop';
  const offsetX = isDesktop ? GAP : null;
  const offsetY = isDesktop ? 1 : null;
  const side = TOOLTIP_SIDE[deviceType];
  const trigger = 'no-trigger';
  const maskClosable = false;
  const mask = true;
  const behaviour = 'FIXED';
  return {
    offsetX,
    offsetY,
    side,
    trigger,
    maskClosable,
    mask,
    behaviour
  };
}
const availableComponents = {};
availableComponents.image_component = withMappedProps(ImageComponent)(imageToProps);
availableComponents.label_component = withMappedProps(StyledLabel)(labelToProps);
availableComponents.generic_info_row = withMappedProps(Media)(mediaToProps);
const localStorageShippingTooltipId = 'TOOLTIP_NO_FREE_SHIPPING';
const getInformationTooltipContent = (information_tooltip, toggleVisibility) => /*#__PURE__*/_jsx(InformationTooltipContent, {
  information_tooltip: information_tooltip,
  toggleVisibility: toggleVisibility
});
const getTooltipCloseButtonProps = (information_tooltip, toggleTooltipVisibility) => {
  if (information_tooltip !== null && information_tooltip !== void 0 && information_tooltip.close_button) {
    const {
      action_type: actionType
    } = information_tooltip.close_button;
    return {
      onClose: () => handleTooltipDefaultAction(actionType, toggleTooltipVisibility),
      showCloseButton: true
    };
  }
  return null;
};
const useTooltipProps = (information_tooltip, tooltip) => {
  const utilsProps = useTooltipUtils();
  const {
    offsetX,
    offsetY
  } = utilsProps;
  const timeFrequency = (information_tooltip === null || information_tooltip === void 0 ? void 0 : information_tooltip.time_frequency) || null;
  const [showTooltip, toggleTooltipVisibility] = useTooltipWithFrequencyStatus(localStorageShippingTooltipId, timeFrequency);
  if (tooltip) {
    return _objectSpread(_objectSpread({}, tooltip), {}, {
      offsetX,
      offsetY,
      availableComponents
    });
  }
  if (information_tooltip) {
    return _objectSpread(_objectSpread({}, utilsProps), {}, {
      content: getInformationTooltipContent(information_tooltip, toggleTooltipVisibility),
      availableComponents,
      visible: !!information_tooltip && showTooltip,
      showCloseButton: false
    }, getTooltipCloseButtonProps(information_tooltip, toggleTooltipVisibility) || {});
  }
  return null;
};
const setTooltipProps = Component => {
  const ComponentWithTooltipProps = props => {
    const {
      information_tooltip,
      tooltip
    } = props;
    const tooltipProps = useTooltipProps(information_tooltip, tooltip);
    return /*#__PURE__*/React.createElement(Component, _extends({}, props, {
      tooltip: tooltipProps
    }));
  };
  ComponentWithTooltipProps.displayName = 'WithTooltipProps';
  return ComponentWithTooltipProps;
};
export { InformationTooltipContent, TOOLTIP_CONSTANTS, Tooltip, TooltipContent, TooltipWrapper, handleTooltipDefaultAction, setTooltipProps, availableComponents as tooltipAvailableComponents, useTooltipCookie, useTooltipProps, useTooltipUtils, useTooltipWithFrequencyStatus, withTooltip };
